import React, { useState } from 'react';
import {
  MapPin, Search, Bell, ChevronDown, Sliders,
  Trophy, Dumbbell, Timer, QrCode,
  CalendarDays, AlertCircle, BadgeCheck, Building,
  Flag, Globe, Plus, Filter, Star, Zap,
  Shield, Crown, Sparkles, Users, Menu,
  X, CheckCircle, Clock, TrendingUp
} from 'lucide-react';

const MembershipView = () => {
  // Estados
  const [searchTerm, setSearchTerm] = useState('');
  const [showExpired, setShowExpired] = useState(false);
  const [showQRCode, setShowQRCode] = useState(null);
  const [notifications, setNotifications] = useState(1);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const memberships = [
    {
      id: 1,
      name: "Elite Fitness Club",
      type: "Gym",
      category: "fitness",
      location: "Downtown NY",
      startDate: "2024-01-01",
      expiryDate: "2024-12-31",
      status: "active",
      price: 49.99,
      originalPrice: 89.99,
      image: "/api/placeholder/800/400",
      membershipId: "EF-2024-001",
      features: ["24/7 Access", "All Classes", "Personal Trainer", "Nutrition Plan"],
      icon: <Dumbbell className="w-6 h-6" />,
      tier: "premium",
      rating: 4.8,
      members: 2840,
      color: "from-orange-500 to-red-500"
    },
    {
      id: 2,
      name: "Green Valley Golf Club",
      type: "Golf",
      category: "sports",
      location: "Long Island",
      startDate: "2024-02-01",
      expiryDate: "2025-01-31",
      status: "active",
      price: 199.99,
      originalPrice: 299.99,
      image: "/api/placeholder/800/400",
      membershipId: "GV-2024-123",
      features: ["Course Access", "Club House", "Equipment Storage", "Pro Lessons"],
      icon: <Flag className="w-6 h-6" />,
      tier: "elite",
      rating: 4.9,
      members: 850,
      color: "from-green-500 to-emerald-600"
    },
    {
      id: 3,
      name: "City Sports Complex",
      type: "Sports Club",
      category: "sports",
      location: "Brooklyn",
      startDate: "2023-06-01",
      expiryDate: "2024-01-31",
      status: "expired",
      price: 79.99,
      originalPrice: 99.99,
      image: "/api/placeholder/800/400",
      membershipId: "CS-2023-456",
      features: ["Multiple Sports", "Events Access", "Locker Room", "Group Training"],
      icon: <Trophy className="w-6 h-6" />,
      tier: "standard",
      rating: 4.6,
      members: 1250,
      color: "from-blue-500 to-purple-600"
    },
    {
      id: 4,
      name: "Wellness Spa Retreat",
      type: "Wellness",
      category: "wellness",
      location: "Manhattan",
      startDate: "2024-03-01",
      expiryDate: "2025-02-28",
      status: "active",
      price: 159.99,
      originalPrice: 220.99,
      image: "/api/placeholder/800/400",
      membershipId: "WS-2024-789",
      features: ["Spa Access", "Massage Therapy", "Meditation Classes", "Healthy Meals"],
      icon: <Sparkles className="w-6 h-6" />,
      tier: "premium",
      rating: 4.7,
      members: 650,
      color: "from-pink-500 to-purple-500"
    }
  ];

  const categories = [
    { id: 'all', name: 'All', icon: <Globe className="w-4 h-4" /> },
    { id: 'fitness', name: 'Fitness', icon: <Dumbbell className="w-4 h-4" /> },
    { id: 'sports', name: 'Sports', icon: <Trophy className="w-4 h-4" /> },
    { id: 'wellness', name: 'Wellness', icon: <Sparkles className="w-4 h-4" /> }
  ];

  const filterMemberships = () => {
    return memberships.filter(membership => {
      const matchesSearch = membership.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        membership.type.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = showExpired ? true : membership.status === 'active';
      const matchesCategory = activeFilter === 'all' || membership.category === activeFilter;
      return matchesSearch && matchesStatus && matchesCategory;
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'elite': return <Crown className="w-4 h-4" />;
      case 'premium': return <Star className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'elite': return 'bg-gradient-to-r from-yellow-400 to-orange-500';
      case 'premium': return 'bg-gradient-to-r from-purple-500 to-pink-500';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  const activeMemberships = memberships.filter(m => m.status === 'active').length;
  const expiredMemberships = memberships.filter(m => m.status === 'expired').length;
  const totalValue = memberships.reduce((sum, m) => sum + (m.status === 'active' ? m.price : 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-red-50 to-pink-50">
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto p-8">
          {/* Header Desktop */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-2">
                My Memberships
              </h1>
              <p className="text-gray-600 text-lg">
                Manage your active subscriptions and explore new opportunities
              </p>
            </div>
            <div className="flex items-center gap-4">
              <button className="relative p-3 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200">
                <Bell className="w-6 h-6 text-gray-700" />
                {notifications > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications}
                  </span>
                )}
              </button>
              <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl font-semibold hover:shadow-lg transition-all duration-200 flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Add Membership
              </button>
            </div>
          </div>

          {/* Stats Cards Desktop */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Active</p>
                  <p className="text-3xl font-bold text-green-500">{activeMemberships}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Expired</p>
                  <p className="text-3xl font-bold text-red-500">{expiredMemberships}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-red-500" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Total</p>
                  <p className="text-3xl font-bold text-blue-500">{memberships.length}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Building className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Monthly Value</p>
                  <p className="text-3xl font-bold text-orange-500">${totalValue.toFixed(0)}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-orange-500" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters Desktop */}
          <div className="flex items-center gap-6 mb-8">
            <div className="flex-1 max-w-md bg-white rounded-2xl border-2 border-orange-100 px-6 py-4 flex items-center shadow-lg">
              <Search className="w-5 h-5 text-gray-400 mr-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search memberships..."
                className="w-full bg-transparent outline-none text-lg"
              />
            </div>
            <div className="flex gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  className={`px-6 py-4 rounded-2xl font-medium transition-all duration-200 flex items-center gap-2 ${activeFilter === category.id
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                      : 'bg-white text-gray-700 hover:bg-orange-50 shadow-lg'
                    }`}
                >
                  {category.icon}
                  {category.name}
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowExpired(!showExpired)}
              className={`px-6 py-4 rounded-2xl font-medium transition-all duration-200 flex items-center gap-2 shadow-lg ${showExpired
                  ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                  : 'bg-white text-gray-700 hover:bg-red-50'
                }`}
            >
              <Timer className="w-5 h-5" />
              Show Expired
            </button>
          </div>

          {/* Memberships Grid Desktop */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
            {filterMemberships().map(membership => (
              <div key={membership.id} className="bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative">
                  <img
                    src={membership.image}
                    alt={membership.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                  {/* Status Badge */}
                  <div className="absolute top-4 right-4">
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-md ${membership.status === 'active'
                        ? 'bg-green-500/90 text-white'
                        : 'bg-red-500/90 text-white'
                      }`}>
                      {membership.status === 'active' ? 'Active' : 'Expired'}
                    </span>
                  </div>

                  {/* Tier Badge */}
                  <div className="absolute top-4 left-4">
                    <div className={`${getTierColor(membership.tier)} text-white px-3 py-2 rounded-full flex items-center gap-2 text-sm font-semibold backdrop-blur-md`}>
                      {getTierIcon(membership.tier)}
                      {membership.tier.charAt(0).toUpperCase() + membership.tier.slice(1)}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="absolute bottom-4 left-4">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-lg">
                      {membership.icon}
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-xl text-gray-800 mb-1">{membership.name}</h3>
                      <p className="text-gray-600 font-medium">{membership.type}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-2xl font-bold text-orange-500">
                          ${membership.price}
                        </span>
                        <span className="text-lg text-gray-400 line-through">
                          ${membership.originalPrice}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">/month</span>
                    </div>
                  </div>

                  {/* Rating and Members */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm font-semibold">{membership.rating}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{membership.members.toLocaleString()} members</span>
                    </div>
                  </div>

                  {/* Location and Expiry */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                      <span className="text-sm">{membership.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <CalendarDays className="w-4 h-4 mr-2 text-orange-500" />
                      <span className="text-sm">
                        Expires: {formatDate(membership.expiryDate)}
                      </span>
                    </div>
                    {membership.status === 'active' && getDaysUntilExpiry(membership.expiryDate) > 0 && (
                      <div className="flex items-center text-orange-600">
                        <Timer className="w-4 h-4 mr-2" />
                        <span className="text-sm font-medium">
                          {getDaysUntilExpiry(membership.expiryDate)} days remaining
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {membership.features.slice(0, 3).map((feature, index) => (
                      <span
                        key={index}
                        className="bg-gradient-to-r from-orange-50 to-red-50 text-orange-700 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                      >
                        <BadgeCheck className="w-3 h-3" />
                        {feature}
                      </span>
                    ))}
                    {membership.features.length > 3 && (
                      <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                        +{membership.features.length - 3} more
                      </span>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    {membership.status === 'active' && (
                      <button
                        onClick={() => setShowQRCode(membership.id)}
                        className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-3 rounded-2xl font-semibold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
                      >
                        <QrCode className="w-4 h-4" />
                        Digital Pass
                      </button>
                    )}
                    <button className="flex-1 border-2 border-orange-200 hover:border-orange-400 hover:bg-orange-50 px-4 py-3 rounded-2xl font-semibold transition-all duration-200 text-gray-700">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filterMemberships().length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-orange-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">No memberships found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
              <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl font-semibold">
                Add New Membership
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden">
        <div className="max-w-md mx-auto bg-gray-50 min-h-screen pb-20">
          {/* Header Mobile */}
          <div className="p-4 space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center">
                  <Building className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-sm text-gray-500">My Memberships</h2>
                  <div className="flex items-center">
                    <span className="font-semibold">Active & Expired</span>
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="relative p-2">
                  <Bell className="w-6 h-6 text-gray-700" />
                  {notifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {notifications}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="p-2"
                >
                  <Filter className="w-6 h-6 text-gray-700" />
                </button>
              </div>
            </div>

            {/* Search Bar Mobile */}
            <div className="bg-white rounded-2xl border-2 border-orange-100 px-4 py-3 flex items-center shadow-lg">
              <Search className="w-5 h-5 text-gray-400 mr-3" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search memberships..."
                className="w-full bg-transparent outline-none"
              />
            </div>

            {/* Category Filters Mobile */}
            {showFilters && (
              <div className="bg-white rounded-2xl p-4 shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Filters</h3>
                  <button onClick={() => setShowFilters(false)}>
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setActiveFilter(category.id)}
                      className={`p-3 rounded-xl font-medium transition-all duration-200 flex items-center gap-2 ${activeFilter === category.id
                          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white'
                          : 'bg-gray-100 text-gray-700'
                        }`}
                    >
                      {category.icon}
                      {category.name}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setShowExpired(!showExpired)}
                  className={`w-full p-3 rounded-xl font-medium transition-all duration-200 flex items-center justify-center gap-2 ${showExpired
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white'
                      : 'bg-gray-100 text-gray-700'
                    }`}
                >
                  <Timer className="w-5 h-5" />
                  Show Expired
                </button>
              </div>
            )}
          </div>

          {/* Stats Summary Mobile */}
          <div className="px-4 mb-6">
            <div className="bg-white rounded-2xl p-4 shadow-lg">
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-500">{activeMemberships}</div>
                  <div className="text-xs text-gray-600">Active</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-500">{expiredMemberships}</div>
                  <div className="text-xs text-gray-600">Expired</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-500">{memberships.length}</div>
                  <div className="text-xs text-gray-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-500">${totalValue.toFixed(0)}</div>
                  <div className="text-xs text-gray-600">Value</div>
                </div>
              </div>
            </div>
          </div>

          {/* Memberships List Mobile */}
          <div className="px-4">
            <div className="space-y-4">
              {filterMemberships().map(membership => (
                <div key={membership.id} className="bg-white rounded-2xl overflow-hidden shadow-lg">
                  <div className="relative">
                    <img
                      src={membership.image}
                      alt={membership.name}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                    <div className="absolute top-3 right-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium backdrop-blur-md ${membership.status === 'active'
                          ? 'bg-green-500/90 text-white'
                          : 'bg-red-500/90 text-white'
                        }`}>
                        {membership.status === 'active' ? 'Active' : 'Expired'}
                      </span>
                    </div>

                    <div className="absolute top-3 left-3">
                      <div className={`${getTierColor(membership.tier)} text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium backdrop-blur-md`}>
                        {getTierIcon(membership.tier)}
                        {membership.tier}
                      </div>
                    </div>

                    <div className="absolute bottom-3 left-3">
                      <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg">
                        {React.cloneElement(membership.icon, { className: "w-5 h-5" })}
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{membership.name}</h3>
                        <p className="text-sm text-gray-600">{membership.type}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-yellow-400 fill-current" />
                            <span className="text-xs font-medium">{membership.rating}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-600">{membership.members.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1">
                          <span className="text-lg font-bold text-orange-500">
                            ${membership.price}
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            ${membership.originalPrice}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">/mo</span>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-gray-600">
                        <MapPin className="w-4 h-4 mr-2 text-orange-500" />
                        <span className="text-sm">{membership.location}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <CalendarDays className="w-4 h-4 mr-2 text-orange-500" />
                        <span className="text-sm">
                          Expires: {formatDate(membership.expiryDate)}
                        </span>
                      </div>
                      {membership.status === 'active' && getDaysUntilExpiry(membership.expiryDate) > 0 && (
                        <div className="flex items-center text-orange-600">
                          <Timer className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">
                            {getDaysUntilExpiry(membership.expiryDate)} days remaining
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {membership.features.slice(0, 2).map((feature, index) => (
                        <span
                          key={index}
                          className="bg-gradient-to-r from-orange-50 to-red-50 text-orange-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                        >
                          <BadgeCheck className="w-3 h-3" />
                          {feature}
                        </span>
                      ))}
                      {membership.features.length > 2 && (
                        <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium">
                          +{membership.features.length - 2} more
                        </span>
                      )}
                    </div>

                    <div className="flex gap-2">
                      {membership.status === 'active' && (
                        <button
                          onClick={() => setShowQRCode(membership.id)}
                          className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center justify-center gap-2 shadow-lg"
                        >
                          <QrCode className="w-4 h-4" />
                          Digital Pass
                        </button>
                      )}
                      <button className="flex-1 border-2 border-orange-200 hover:border-orange-400 hover:bg-orange-50 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200">
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filterMemberships().length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-100 to-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-orange-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">No memberships found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
                <button className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-xl font-medium">
                  Add Membership
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl p-6 lg:p-8 w-full max-w-sm lg:max-w-md shadow-2xl transform transition-all duration-300">
            <div className="text-center mb-6">
              <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <QrCode className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-2">Digital Membership Pass</h3>
              <p className="text-gray-600">Show this QR code at the facility entrance</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 lg:p-8 rounded-2xl mb-6">
              <div className="bg-white p-4 rounded-xl shadow-inner">
                <img
                  src="/api/placeholder/300/300"
                  alt="QR Code"
                  className="w-full aspect-square object-contain"
                />
              </div>
            </div>

            <div className="text-center mb-6">
              <p className="text-sm text-gray-600 mb-2">Membership ID</p>
              <p className="font-mono text-lg font-bold text-gray-800 bg-gray-100 px-4 py-2 rounded-xl">
                {memberships.find(m => m.id === showQRCode)?.membershipId}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowQRCode(null)}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 rounded-2xl font-semibold transition-all duration-200"
              >
                Close
              </button>
              <button className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 rounded-2xl font-semibold transition-all duration-200 flex items-center justify-center gap-2">
                <span>Share</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button - Mobile */}
      <div className="lg:hidden">
        <button className="fixed bottom-24 right-6 w-14 h-14 bg-gradient-to-r from-orange-500 to-red-500 rounded-full shadow-2xl flex items-center justify-center z-40 hover:shadow-3xl transition-all duration-200 transform hover:scale-110">
          <Plus className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Background Decorative Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-orange-200/30 to-red-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-pink-200/30 to-purple-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-orange-100/20 to-red-100/20 rounded-full blur-3xl"></div>
      </div>
    </div>
  );
};

export default MembershipView;